import { provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

const AUTOLOGIN_PATH = '/autologin';
const AUTOLOGIN_TARGET = '/?registrationSuccess=1';
const AFTER_LOGIN_PATH = '/after-login';
const JWT_PARTS_COUNT = 3;

function consumeAutologinFragment(): void {
  if (!window.location.pathname.replace(/\/$/, '').endsWith(AUTOLOGIN_PATH)) {
    return;
  }

  logAutologinStep('fragment:enter', {
    path: window.location.pathname,
    hasHash: Boolean(window.location.hash),
  });

  const fragment = window.location.hash.replace(/^#/, '');

  if (!fragment) {
    logAutologinStep('fragment:missing');
    return;
  }

  const params = new URLSearchParams(fragment);
  const accessToken = readParamString(params, 'access_token', 'accessToken');
  const target = sanitizeAutologinTarget(readParamString(params, 'target'));
  const afterLoginTarget = buildAfterLoginTarget(target);

  logAutologinStep('fragment:parsed', {
    hasAccessToken: Boolean(accessToken),
    hasRefreshToken: Boolean(readParamString(params, 'refresh_token', 'refreshToken')),
    hasIdToken: Boolean(readParamString(params, 'id_token', 'idToken')),
    target,
  });

  if (!accessToken) {
    logAutologinStep('fragment:missing-access-token', { target, afterLoginTarget });
    return;
  }

  const accessTokenPayload = parseJwtPayload(accessToken);

  if (!accessTokenPayload) {
    clearAutologinTokens();
    logInvalidAutologinToken();
    window.history.replaceState({}, document.title, afterLoginTarget);
    logAutologinStep('fragment:invalid-token', { target, afterLoginTarget });
    return;
  }

  logAutologinStep('fragment:valid-token', summarizeAccessToken(accessToken, accessTokenPayload));
  storeAutologinTokens(params, accessToken, accessTokenPayload);
  window.history.replaceState({}, document.title, afterLoginTarget);
  logAutologinStep('fragment:target-replaced', { target, afterLoginTarget });
}

function storeAutologinTokens(params: URLSearchParams, accessToken: string, accessTokenPayload: Record<string, unknown>): void {
  const now = Date.now();
  const expiresIn = readParamNumber(params, 'expires_in', 'expiresIn') ?? readJwtExpiresIn(accessTokenPayload);
  const idToken = readParamString(params, 'id_token', 'idToken');
  const refreshToken = readParamString(params, 'refresh_token', 'refreshToken');
  const scopes = readScopes(readParamString(params, 'scope'), accessTokenPayload);

  logAutologinStep('storage:write:start', {
    expiresIn,
    hasIdToken: Boolean(idToken),
    hasRefreshToken: Boolean(refreshToken),
    scopes,
  });

  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('access_token_stored_at', String(now));
  setOptionalStorageValue('refresh_token', refreshToken);
  setOptionalStorageValue('id_token', idToken);
  setOptionalStorageValue('token_type', readParamString(params, 'token_type', 'tokenType') ?? 'Bearer');

  if (expiresIn !== null) {
    localStorage.setItem('expires_at', String(now + expiresIn * 1000));
  } else {
    localStorage.removeItem('expires_at');
  }

  if (idToken) {
    const idTokenPayload = parseJwtPayload(idToken);
    const idTokenExpiresIn = idTokenPayload ? readJwtExpiresIn(idTokenPayload) : null;

    if (idTokenExpiresIn !== null) {
      localStorage.setItem('id_token_expires_at', String(now + idTokenExpiresIn * 1000));
    } else {
      localStorage.removeItem('id_token_expires_at');
    }
  } else {
    localStorage.removeItem('id_token_expires_at');
  }

  if (scopes.length) {
    localStorage.setItem('granted_scopes', JSON.stringify(scopes));
  } else {
    localStorage.removeItem('granted_scopes');
  }

  logAutologinStep('storage:write:success', {
    hasAccessToken: Boolean(localStorage.getItem('access_token')),
    hasRefreshToken: Boolean(localStorage.getItem('refresh_token')),
    grantedScopes: localStorage.getItem('granted_scopes'),
  });
}

function readParamString(params: URLSearchParams, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = params.get(key);

    const normalizedValue = normalizeOptionalTokenString(value);

    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return null;
}

function readParamNumber(params: URLSearchParams, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = params.get(key);

    if (!value?.trim()) {
      continue;
    }

    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
}

function setOptionalStorageValue(key: string, value: string | null): void {
  const normalizedValue = normalizeOptionalTokenString(value);

  if (normalizedValue) {
    localStorage.setItem(key, normalizedValue);
    return;
  }

  localStorage.removeItem(key);
}

function normalizeOptionalTokenString(value: string | null): string | null {
  const trimmedValue = value?.trim();

  if (!trimmedValue || ['null', 'undefined'].includes(trimmedValue.toLowerCase())) {
    return null;
  }

  return trimmedValue;
}

function sanitizeStoredAutologinTokens(): void {
  ['refresh_token', 'id_token', 'token_type'].forEach(key => {
    if (!normalizeOptionalTokenString(localStorage.getItem(key))) {
      logAutologinStep('storage:sanitize-remove', { key });
      localStorage.removeItem(key);
    }
  });
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  const tokenParts = token.split('.');

  if (tokenParts.length !== JWT_PARTS_COUNT) {
    return null;
  }

  return parseBase64UrlJson(tokenParts[1]);
}

function readJwtExpiresIn(payload: Record<string, unknown>): number | null {
  const expiresAt = payload['exp'];

  if (typeof expiresAt !== 'number') {
    return null;
  }

  return Math.max(Math.floor(expiresAt - Date.now() / 1000), 0);
}

function parseBase64UrlJson(value: string): Record<string, unknown> | null {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedValue = normalizedValue.padEnd(Math.ceil(normalizedValue.length / 4) * 4, '=');

  try {
    const decodedValue = decodeURIComponent(
      globalThis
        .atob(paddedValue)
        .split('')
        .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const parsedValue = JSON.parse(decodedValue) as unknown;

    if (typeof parsedValue === 'object' && parsedValue !== null && !Array.isArray(parsedValue)) {
      return parsedValue as Record<string, unknown>;
    }

    return null;
  } catch {
    return null;
  }
}

function readScopes(scope: string | null, accessTokenPayload: Record<string, unknown>): string[] {
  const explicitScopes = parseScopeString(scope);

  if (explicitScopes.length) {
    return explicitScopes;
  }

  return readJwtStringArray(accessTokenPayload, 'scope');
}

function parseScopeString(scope: string | null): string[] {
  if (!scope) {
    return [];
  }

  try {
    const parsedScope = JSON.parse(scope) as unknown;

    if (Array.isArray(parsedScope)) {
      return parsedScope.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
    }
  } catch {
    return scope.split(' ').filter(Boolean);
  }

  return scope.split(' ').filter(Boolean);
}

function readJwtStringArray(record: Record<string, unknown>, key: string): string[] {
  const value = record[key];

  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  }

  if (typeof value === 'string' && value.trim()) {
    return value.split(' ').filter(Boolean);
  }

  return [];
}

function clearAutologinTokens(): void {
  [
    'access_token',
    'access_token_stored_at',
    'expires_at',
    'granted_scopes',
    'id_token',
    'id_token_claims_obj',
    'id_token_expires_at',
    'id_token_stored_at',
    'refresh_token',
    'token_type',
  ].forEach(key => localStorage.removeItem(key));
}

function logInvalidAutologinToken(): void {
  if (environment.production) {
    return;
  }

  console.warn('[Autologin] Ignored invalid access token from URL fragment');
}

function logAutologinStep(step: string, payload: Record<string, unknown> = {}): void {
  if (environment.production) {
    return;
  }

  console.log(`[Autologin] ${step}`, payload);
}

function summarizeAccessToken(accessToken: string, payload: Record<string, unknown>): Record<string, unknown> {
  return {
    accessTokenLength: accessToken.length,
    accessTokenStart: `${accessToken.slice(0, 24)}...`,
    iss: readParamLikeString(payload, 'iss'),
    aud: readJwtStringArray(payload, 'aud'),
    scope: readJwtStringArray(payload, 'scope'),
    exp: typeof payload['exp'] === 'number' ? payload['exp'] : null,
  };
}

function readParamLikeString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];

  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function sanitizeAutologinTarget(target: string | null): string {
  if (!target || !target.startsWith('/') || target.startsWith('//')) {
    return AUTOLOGIN_TARGET;
  }

  return target;
}

function buildAfterLoginTarget(target: string): string {
  const params = new URLSearchParams();

  params.set('target', target);

  return `${AFTER_LOGIN_PATH}?${params.toString()}`;
}

sanitizeStoredAutologinTokens();
consumeAutologinFragment();

platformBrowserDynamic()
  .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
  .catch(err => console.error(err));
